import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  getFavorites,
  logoutUser,
  updateProfile,
  changePassword,
} from "../utils/auth";
import { getSeriesById } from "../utils/seriesUtils";
import { getAllSubmissions } from "../utils/submissionUtils";
import { useLang } from "../i18n/LanguageContext";
import { handleImgError } from "../utils/thumbnailUtils";
import "../styles/styles.css";

const AVATARS = [
  "🎬","🤖","🚀","🌌","🎭","📚","🎨","🎵","🦾","🧠",
  "👾","🌟","🔮","🎮","🦊","🐙","🌈","🔥","⚡","💎",
];

/* ── Sección colapsable ── */
const Section = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="profile-section">
      <button className="profile-section-toggle" onClick={() => setOpen(!open)}>
        <h2 className="profile-section-title">{title}</h2>
        <span className="section-chevron">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="profile-section-body">{children}</div>}
    </div>
  );
};

const Profile = ({ user, setUser }) => {
  const navigate  = useNavigate();
  const { tr }    = useLang();
  const p         = tr.profile;

  // Estado local
  const [username, setUsername]   = useState(user?.username || "");
  const [avatar,   setAvatar]     = useState(user?.avatar   || "🎬");
  const [savedMsg, setSavedMsg]   = useState("");

  const [curPwd,   setCurPwd]     = useState("");
  const [newPwd,   setNewPwd]     = useState("");
  const [confPwd,  setConfPwd]    = useState("");
  const [pwdMsg,   setPwdMsg]     = useState("");

  const [favorites,    setFavorites]    = useState([]);
  const [submissions,  setSubmissions]  = useState([]);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    setUsername(user.username || "");
    setAvatar(user.avatar || "🎬");
    const favIds = getFavorites(user.email);
    setFavorites(favIds.map((id) => getSeriesById(id)).filter(Boolean));
    const all = getAllSubmissions();
    setSubmissions(all.filter((s) => s.submittedBy === user.email));
  }, [user, navigate]);

  if (!user) return null;

  /* ── Guardar nombre / avatar ── */
  const handleSaveProfile = () => {
    updateProfile(user.email, { username, avatar });
    const updated = { ...user, username, avatar };
    localStorage.setItem("currentUser", JSON.stringify(updated));
    setUser(updated);
    setSavedMsg(p.savedOk);
    setTimeout(() => setSavedMsg(""), 2000);
  };

  /* ── Cambiar contraseña ── */
  const handleChangePassword = (e) => {
    e.preventDefault();
    if (newPwd !== confPwd) { setPwdMsg(p.passwordError); return; }
    const ok = changePassword(user.email, curPwd, newPwd);
    if (!ok) { setPwdMsg(p.currentPasswordError); return; }
    setPwdMsg(p.passwordOk);
    setCurPwd(""); setNewPwd(""); setConfPwd("");
    setTimeout(() => setPwdMsg(""), 2500);
  };

  const handleLogout = () => {
    logoutUser();
    setUser(null);
    navigate("/");
  };

  const roleLabel = (p.roles || {})[user.role] || user.role;
  const roleColor = { admin: "#ff4500", creator: "#00ffe5", user: "#b3b3b3" }[user.role] || "#b3b3b3";
  const displayName = user.username || user.email;

  return (
    <div className="profile-page">

      {/* ── CABECERA ── */}
      <div className="profile-header">
        <div className="profile-avatar-display">{avatar}</div>
        <div className="profile-info">
          <h1 className="profile-email">{displayName}</h1>
          {user.username && <p className="profile-sub-email">{user.email}</p>}
          <span className="profile-role" style={{ color: roleColor }}>● {roleLabel}</span>
        </div>
        <button className="btn-logout-profile" onClick={handleLogout}>{p.logout}</button>
      </div>

      <div className="profile-content">

        {/* ── AVATAR + NOMBRE ── */}
        <Section title={`👤 ${p.username} & ${p.avatar}`} defaultOpen={true}>
          <div className="avatar-picker">
            {AVATARS.map((em) => (
              <button
                key={em}
                className={`avatar-option ${em === avatar ? "selected" : ""}`}
                onClick={() => setAvatar(em)}
              >
                {em}
              </button>
            ))}
          </div>
          <div className="username-row">
            <input
              className="profile-input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={p.usernamePlaceholder}
              maxLength={30}
            />
            <button className="btn-primary btn-sm" onClick={handleSaveProfile}>
              {p.saveUsername}
            </button>
          </div>
          {savedMsg && <p className="profile-feedback ok">{savedMsg}</p>}
        </Section>

        {/* ── CAMBIAR CONTRASEÑA ── */}
        <Section title={`🔒 ${p.changePassword}`}>
          <form className="pwd-form" onSubmit={handleChangePassword}>
            <input
              className="profile-input"
              type="password"
              placeholder={p.currentPassword}
              value={curPwd}
              onChange={(e) => setCurPwd(e.target.value)}
              required
            />
            <input
              className="profile-input"
              type="password"
              placeholder={p.newPassword}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              required
              minLength={6}
            />
            <input
              className="profile-input"
              type="password"
              placeholder={p.confirmNewPassword}
              value={confPwd}
              onChange={(e) => setConfPwd(e.target.value)}
              required
            />
            <button type="submit" className="btn-primary btn-sm">{p.updatePassword}</button>
          </form>
          {pwdMsg && (
            <p className={`profile-feedback ${pwdMsg === p.passwordOk ? "ok" : "err"}`}>
              {pwdMsg}
            </p>
          )}
        </Section>

        {/* ── ACCIONES RÁPIDAS ── */}
        <Section title={`⚡ ${p.quickActions}`} defaultOpen={true}>
          <div className="profile-actions">
            <Link to="/creators" className="profile-action-card">
              <span className="action-icon">🎬</span>
              <span>{p.sendPitch}</span>
            </Link>
            {(user.role === "creator" || user.role === "admin") && (
              <Link to="/upload" className="profile-action-card">
                <span className="action-icon">⬆️</span>
                <span>{p.uploadContent}</span>
              </Link>
            )}
            {user.role === "admin" && (
              <>
                <Link to="/admin/submissions" className="profile-action-card">
                  <span className="action-icon">📋</span>
                  <span>{tr.nav.pitches}</span>
                </Link>
                <Link to="/admin/users" className="profile-action-card">
                  <span className="action-icon">👥</span>
                  <span>{tr.nav.users}</span>
                </Link>
                <Link to="/admin/videos" className="profile-action-card">
                  <span className="action-icon">🎥</span>
                  <span>{tr.nav.videos}</span>
                </Link>
              </>
            )}
          </div>
        </Section>

        {/* ── FAVORITOS ── */}
        <Section title={`${p.favorites} (${favorites.length})`}>
          {favorites.length === 0 ? (
            <p className="profile-empty">{p.noFavorites} <Link to="/">{p.explore}</Link></p>
          ) : (
            <div className="profile-favorites-grid">
              {favorites.map((s) => (
                <Link key={s.id} to={`/series/${s.id}`} className="profile-fav-card">
                  <img src={s.thumbnail} alt={s.title} onError={(e) => handleImgError(e, s.title, 400, 225)} />
                  <p>{s.title}</p>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* ── PROPUESTAS ── */}
        <Section title={`${p.proposals} (${submissions.length})`}>
          {submissions.length === 0 ? (
            <p className="profile-empty">{p.noPitches} <Link to="/creators">{p.sendNow}</Link></p>
          ) : (
            <div className="profile-submissions">
              {submissions.map((s) => (
                <div key={s.id} className="submission-card">
                  <div className="submission-header">
                    <h3>{s.title}</h3>
                    <span className={`submission-status status-${s.status}`}>
                      {p[`status${s.status.charAt(0).toUpperCase() + s.status.slice(1)}`] || s.status}
                    </span>
                  </div>
                  <p className="submission-synopsis">{s.synopsis}</p>
                  <div className="submission-meta">
                    <span>{s.genres?.join(", ")}</span>
                    <span>{new Date(s.submittedAt).toLocaleDateString()}</span>
                  </div>
                  {s.comments && <p className="submission-comments">💬 {s.comments}</p>}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ── FAQ ── */}
        <Section title={`❓ ${p.faq}`}>
          <div className="faq-list">
            {(p.faqItems || []).map((item, i) => (
              <FaqItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </Section>

        {/* ── CONTACTO ── */}
        <Section title={`✉️ ${p.contact}`}>
          <div className="contact-section">
            <p>{p.contactEmail} <a href="mailto:aiveoaicinema@gmail.com" className="contact-link">aiveoaicinema@gmail.com</a></p>
            <div className="contact-links">
              <a href="https://www.aiveo.net" target="_blank" rel="noreferrer" className="contact-chip">🌐 www.aiveo.net</a>
              <a href="mailto:aiveoaicinema@gmail.com" className="contact-chip">📧 Email</a>
            </div>
          </div>
        </Section>

      </div>
    </div>
  );
};

/* ── FAQ colapsable individual ── */
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button className="faq-q" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && <p className="faq-a">{a}</p>}
    </div>
  );
};

export default Profile;
